import { db, collection, query, where, getDocs, doc, getDoc, functions } from "../../firebaseClient";
import { formatDate, parseDate } from "../utils/dateUtils";
import { ApprovalRequest, ContractDoc } from "../types/legalOps";
import { isApprovalPendingForUser, SummaryUserKey } from "../utils/approvalHelpers";
import { ApprovalFlow } from "../types/approval";
import { getRandomWarmTip } from "../utils/getRandomWarmTip";

// --- Types ---

export interface SummaryCounts {
  contracts: number;
  seals: number;
  others: number; 
}

export interface PendingItem {
  id: string;          
  title: string;
  module: "contract" | "seal" | "case" | "template" | "governance" | "other";
}

export interface PendingGrouped {
  contracts: PendingItem[];
  seals: PendingItem[];
  cases: PendingItem[];
  templates: PendingItem[];
  governance: PendingItem[];
  others: PendingItem[];
}

export interface DueItem {
  title: string;
  date: string;
  type: string;
}

export interface DueSoonGroup {
  today: DueItem[];
  within30: DueItem[];
  within60: DueItem[];
}

export interface UserRealtimeSummary {
  userKey: "kevin" | "lisa" | "irene";
  displayName: string;
  counts: SummaryCounts;
  dateLabel: string;
  agenda: string[]; 
  dueSoon: DueSoonGroup;
  pendingList: PendingGrouped;
  encouragement: string;
}

export interface DailySummaryResult {
  generatedAt: string;
  users: UserRealtimeSummary[];
  emailStatus?: { status: string; info?: string; time?: any };
}

const USERS: { key: SummaryUserKey; displayName: string; role: string }[] = [
  { key: "kevin", displayName: "Kevin (法務部)", role: "legal" },
  { key: "lisa", displayName: "Lisa (主管)", role: "manager" },
  { key: "irene", displayName: "Irene (主管)", role: "manager" },
];

const addDaysDate = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    result.setHours(0, 0, 0, 0);
    return result;
};

const classifyDueItem = (targetDateStr: string, today: Date, d30: Date, d60: Date): "today" | "within30" | "within60" | null => {
    if (!targetDateStr) return null;
    const t = parseDate(targetDateStr);
    if (!t) return null;
    t.setHours(0,0,0,0);
    const tTime = t.getTime();
    if (tTime === today.getTime()) return "today";
    if (tTime > today.getTime() && tTime <= d30.getTime()) return "within30";
    if (tTime > d30.getTime() && tTime <= d60.getTime()) return "within60";
    return null;
};

// --- Helper: Fetch Flows Batch ---
async function fetchApprovalFlows(requests: ApprovalRequest[]): Promise<Record<string, ApprovalFlow>> {
    if (requests.length === 0) return {};
    
    const flowMap: Record<string, ApprovalFlow> = {};
    const promises = requests.map(async (req) => {
        let moduleKey = req.module;
        if (moduleKey === 'contractDraft') moduleKey = 'CONTRACT_REVIEW'; 
        else if (moduleKey === 'sealRequest') moduleKey = 'SEAL_REQUEST';
        else if (moduleKey === 'govLetter') moduleKey = 'OFFICIAL_REPLY';
        
        const fid = `${moduleKey}_${req.id}`;
        try {
            const snap = await getDoc(doc(db, "approvalFlows", fid));
            if (snap.exists) {
                flowMap[req.id] = snap.data() as ApprovalFlow;
            }
        } catch (e) { }
    });

    await Promise.all(promises);
    return flowMap;
}

/**
 * Calculates dashboard statistics based on REAL-TIME Firestore data.
 */
export async function getTodaySummary(): Promise<DailySummaryResult> {
  const now = new Date();
  now.setHours(0, 0, 0, 0); 
  const dateStr = formatDate(now); 
  const dateLabel = `${dateStr} (${['日','一','二','三','四','五','六'][now.getDay()]})`;

  const datePlus30 = addDaysDate(now, 30);
  const datePlus60 = addDaysDate(now, 60);
  const strNext60 = formatDate(datePlus60);

  try {
    const activeStatuses = ['SUBMITTED', 'IN_REVIEW', 'PENDING_LEGAL', 'WAITING_APPROVAL', 'PENDING_MANAGER'];
    
    const [snapReq, snapTemplates, snapGov, snapCal, snapContracts] = await Promise.all([
        getDocs(query(collection(db, "approval_requests"), where("status", "in", activeStatuses))),
        getDocs(query(collection(db, "contract_templates"), where("status", "in", ["IN_REVIEW", "PENDING_REVIEW"]))),
        getDocs(query(collection(db, "governance_core_docs"), where("status", "in", ["in_review", "IN_REVIEW"]))),
        getDocs(query(collection(db, "calendarEvents"), where("date", "==", dateStr))),
        getDocs(query(collection(db, "legal_contracts"), where("status", "==", "生效中"))) 
    ]);

    const approvalRequests = snapReq.docs.map(d => ({ id: d.id, ...d.data() } as ApprovalRequest));
    const activeContracts = snapContracts.docs
        .map(d => ({ id: d.id, ...d.data() } as ContractDoc))
        .filter(c => c.periodEnd && c.periodEnd >= dateStr && c.periodEnd <= strNext60);

    const flowMap = await fetchApprovalFlows(approvalRequests);
    const users: UserRealtimeSummary[] = [];

    for (const u of USERS) {
        const pending: PendingGrouped = { contracts: [], seals: [], cases: [], templates: [], governance: [], others: [] };
        
        const userApprovals = approvalRequests.filter(req => {
            const flow = flowMap[req.id];
            return isApprovalPendingForUser(req, u.key, flow);
        });

        userApprovals.forEach(req => {
            const item: PendingItem = { id: req.docCode || req.id.slice(0,8), title: req.title, module: "other" };
            if (req.module === 'contractDraft' || req.module === 'contractReview') { 
                item.module = "contract"; 
                pending.contracts.push(item); 
            } else if (req.module === 'sealRequest') { 
                item.module = "seal"; 
                pending.seals.push(item); 
            } else { 
                pending.others.push(item); 
            }
        });

        if (u.role === 'legal' || u.role === 'manager') {
             snapTemplates.docs.forEach((t: any) => pending.templates.push({ id: t.data().docCode, title: t.data().title, module: "template" }));
             snapGov.docs.forEach((g: any) => pending.governance.push({ id: g.data().docNo, title: g.data().title, module: "governance" }));
        }

        const dueSoon: DueSoonGroup = { today: [], within30: [], within60: [] };
        activeContracts.forEach(c => {
            if (c.periodEnd) {
                const bucket = classifyDueItem(c.periodEnd, now, datePlus30, datePlus60);
                if (bucket) dueSoon[bucket].push({ title: c.title, date: c.periodEnd, type: "contract" });
            }
        });

        const agenda = snapCal.docs.map(d => `[${d.data().type}] ${d.data().title}`);
        if (agenda.length === 0) agenda.push("無行程");

        users.push({
            userKey: u.key,
            displayName: u.displayName,
            counts: {
                contracts: pending.contracts.length,
                seals: pending.seals.length,
                others: pending.cases.length + pending.templates.length + pending.governance.length + pending.others.length
            },
            dateLabel,
            agenda,
            dueSoon,
            pendingList: pending,
            encouragement: getRandomWarmTip()
        });
    }

    return {
      generatedAt: new Date().toISOString(),
      users
    };

  } catch (error) {
    console.error("[DailySummaryService] Error:", error);
    return { generatedAt: new Date().toISOString(), users: [] };
  }
}

/**
 * Triggers the daily summary email by calling Cloud Function directly.
 */
export async function triggerDailySummaryEmail(to = 'kevinckc1983@gmail.com'): Promise<void> {
    const fn = functions.httpsCallable('sendDailySummaryNow');
    try {
        const res = await fn({ to });
        const data = res.data as any;
        if (!data?.ok) {
            throw new Error("Server returned unsuccessful status");
        }
    } catch (e: any) {
        console.error("Failed to send summary email", e);
        throw new Error(e.message || "Failed to trigger email");
    }
}
export { getDashboardTodosOnce as getTodaySummary };
